import {tokenizeQuery} from '@github-ui/filter-query'
import {isStaff} from '@github-ui/stats'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useEffect, useRef} from 'react'

function isIssueSearchWithFreeText(query: string): boolean {
  const segments = tokenizeQuery(query)
  return (
    segments.some(segment => segment.type === 'text' && segment.value.trim()) &&
    segments.some(
      segment =>
        segment.type === 'filter' &&
        segment.key.toLowerCase() === 'is' &&
        segment.values.some(value => value.toLowerCase() === 'issue'),
    )
  )
}

/**
 * Hook to track search success metrics for issue searches.
 * Only tracks searches that include "is:issue" and have free-text content.
 *
 * Each qualifying search is assigned a unique ID that's included in both:
 * - The initial search event (search.performed_with_freetext)
 * - Any subsequent success events (search.successful_search_action)
 *
 * This allows linking searches to outcomes in analytics for correlation analysis.
 */
export function useRepoIssuesSearchMetrics(
  query: string | null,
  issueSearchType: string | undefined,
  bulkJobId: string | null,
) {
  const {sendAnalyticsEvent} = useAnalytics()
  const previousQueryRef = useRef(query)
  const activeSearchRef = useRef<{
    query: string
    searchId: string
    startedAt: number
    issueSearchType?: string
    eventSent: boolean
  } | null>(null)

  // watches for a new search: whenever the query changes checks if its free-text. if so, saves id and starts the timer
  useEffect(() => {
    if (query === previousQueryRef.current) return

    previousQueryRef.current = query
    if (query) {
      if (isIssueSearchWithFreeText(query)) {
        activeSearchRef.current = {
          query,
          searchId: crypto.randomUUID(),
          startedAt: Date.now(),
          eventSent: false,
        }
      } else {
        activeSearchRef.current = null
      }
    } else {
      activeSearchRef.current = null
    }
  }, [query])

  // gets issueSearchType from the backend, enriches the previously saved search with it and sends an analytics event if a free-text search is active
  useEffect(() => {
    const search = activeSearchRef.current
    if (!search || issueSearchType === undefined || search.eventSent) return

    search.issueSearchType = issueSearchType
    search.eventSent = true
    sendAnalyticsEvent('search.performed_with_freetext', 'SEARCH_METRICS', {
      query: search.query,
      search_id: search.searchId,
      is_staff: isStaff(),
      issue_search_type: search.issueSearchType,
    })
  }, [issueSearchType, query, sendAnalyticsEvent])

  // records what the user did after searching (opened row, bulk update or nothing)
  const trackSearchAction = useCallback(
    (action: 'bulk_action' | 'issue_navigation', position?: number) => {
      const search = activeSearchRef.current
      if (!search) return

      sendAnalyticsEvent('search.successful_search_action', 'SEARCH_METRICS', {
        query: search.query,
        search_id: search.searchId,
        is_staff: isStaff(),
        issue_search_type: search.issueSearchType ?? 'UNKNOWN',
        action,
        time_to_action_ms: Date.now() - search.startedAt,
        ...(position !== undefined ? {position: position + 1} : {}),
      })
      activeSearchRef.current = null
    },
    [sendAnalyticsEvent],
  )

  // automatically tracks bulk actions when a bulk job is created
  useEffect(() => {
    if (bulkJobId) trackSearchAction('bulk_action')
  }, [bulkJobId, trackSearchAction])

  // tracks when a user selects a row in the search results, called from the row
  const trackRowSelection = useCallback(
    (type: 'Issue' | 'PullRequest') => sendAnalyticsEvent('search_results.select_row', 'SEARCH_RESULT_ROW', {type}),
    [sendAnalyticsEvent],
  )
  // tracks when a user navigates to an issue from the search results, called from the nav
  const trackIssueNavigation = useCallback(
    (position: number) => trackSearchAction('issue_navigation', position),
    [trackSearchAction],
  )

  return {trackRowSelection, trackIssueNavigation}
}
